/**
 * TrustyBytes Central SSO Integration Utility
 * Supports both Localhost (development) and Live (production) seamlessly.
 * Based on TrustyBytes Central SSO Integration Guide (OAuth 2.0 / OpenID Connect)
 */

export interface SsoConfig {
  authUrl: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  /** Allowed email domains — only these may sign in via SSO. */
  allowedEmailDomains: string[];
}

/**
 * Email domains permitted to sign in with the TrustyBytes Central SSO.
 * Anyone whose Google account email does not end with one of these domains is
 * rejected (both here, as a fast user-facing guard, and again server-side,
 * which is the authoritative check). Defaults to the organization domain.
 */
export const TRUSTYBYTES_EMAIL_DOMAINS: string[] =
  (import.meta.env.VITE_SSO_ALLOWED_DOMAINS || 'trustybytes.in,trustybytes.com')
    .split(',')
    .map((d: string) => d.trim().toLowerCase().replace(/^\./, ''))
    .filter(Boolean);

/** True if the given email belongs to an allowed organization domain. */
export function isAllowedSsoEmail(email: string | undefined | null): boolean {
  if (!email || !email.includes('@')) return false;
  const domain = email.split('@')[1].toLowerCase().replace(/^\./, '');
  return TRUSTYBYTES_EMAIL_DOMAINS.includes(domain);
}

/**
 * Returns the current SSO configuration dynamically adapting to both Localhost and Live
 */
export function getSsoConfig(): SsoConfig {
  const authUrl = import.meta.env.VITE_SSO_AUTH_URL || 'https://auth.trustybytes.com/api';
  const scope = import.meta.env.VITE_SSO_SCOPE || 'openid profile email';

  // Dynamic origin detection: Automatically resolves to http://localhost:3000 in dev
  // or https://teamone.trustybytes.com in production.
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
  const isCurrentLocal = currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1');

  // Automatically select TB_CL_14 for Local and TB_CL_15 for Production
  const defaultClientId = isCurrentLocal ? 'TB_CL_14' : 'TB_CL_15';
  const clientId = import.meta.env.VITE_SSO_CLIENT_ID || defaultClientId;
  
  const envRedirectUri = import.meta.env.VITE_SSO_REDIRECT_URI;
  
  // Use the explicitly configured redirect URI whenever it is set. It was
  // derived from the exact origin Central Auth has registered for this client,
  // so trusting it (instead of re-deriving from window.location) keeps the
  // callback URL stable across deploys. Fall back to <origin>/auth/callback.
  let redirectUri = envRedirectUri && envRedirectUri.trim()
    ? envRedirectUri.trim()
    : `${currentOrigin}/auth/callback`;

  return {
    authUrl,
    clientId,
    redirectUri,
    scope,
    allowedEmailDomains: TRUSTYBYTES_EMAIL_DOMAINS,
  };
}

/**
 * Generates a cryptographically strong random state string to prevent CSRF attacks
 */
export function generateRandomState(length: number = 32): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let state = '';
  const randomValues = new Uint8Array(length);
  window.crypto.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    state += charset[randomValues[i] % charset.length];
  }
  return state;
}

/**
 * Initiates the Google SSO Login flow
 * Generates CSRF state, stores it in sessionStorage, and redirects user to Central Auth
 */
export function initiateGoogleSso(): void {
  const config = getSsoConfig();
  const state = generateRandomState();

  // Store CSRF state and current origin in sessionStorage for validation upon callback
  sessionStorage.setItem('sso_csrf_state', state);
  sessionStorage.setItem('sso_initial_origin', window.location.origin);

  // Construct OAuth 2.0 authorize URL
  const params = new URLSearchParams({
    clientId: config.clientId,
    redirectUri: config.redirectUri,
    response_type: 'code',
    scope: config.scope,
    state: state,
  });

  const authorizeUrl = `${config.authUrl.replace(/\/+$/, '')}/login/oauth2/authorize?${params.toString()}`;
  
  // Redirect to Central Auth
  window.location.href = authorizeUrl;
}

/**
 * Refreshes an expired access token using the refresh token (Step 4 in SSO Guide)
 */
export async function refreshSsoToken(refreshToken: string, clientSecret?: string): Promise<{ accessToken: string; refreshToken?: string } | null> {
  const config = getSsoConfig();
  try {
    const response = await fetch(`${config.authUrl.replace(/\/+$/, '')}/login/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grantType: 'refresh_token',
        clientId: config.clientId,
        clientSecret: clientSecret || '',
        refreshToken: refreshToken,
      }),
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed with status ${response.status}`);
    }

    const data = await response.json();
    return {
      accessToken: data.access_token || data.accessToken || data.token,
      refreshToken: data.refresh_token || data.refreshToken || refreshToken,
    };
  } catch (error) {
    console.error('Error refreshing SSO token:', error);
    return null;
  }
}

/**
 * Helper to decode JWT payload without external library
 */
export function parseJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.warn('Failed to parse JWT payload:', e);
    return null;
  }
}
